import { chronicleSessionIdAtom } from '@/atoms/chronicle';
import {
  trackForceUpdateDeferred,
  trackForceUpdateInitiated,
  trackForceUpdateViewed,
} from '@/lib/analytics/scribe/force-update';
import { trackTauriUpdate } from '@/lib/analytics/scribe/tauri';
import { store } from '@/store';
import type {
  ForceUpdatePolicy,
  PolicyEvaluationResult,
} from '@/tauri/force-update/forceUpdatePolicy';
import { POLICY_EVALUATION } from '@/tauri/force-update/forceUpdatePolicy';
import {
  evaluatePolicy,
  fetchPolicyFromRemoteConfig,
} from '@/tauri/force-update/policyEvaluator';
import {
  checkUpdateForFeed,
  installUpdateForFeed,
  resolveUpdaterFeed,
} from '@/tauri/updater/feed';
import { updateFailureMessages } from '@/tauri/updater/updater.messages';
import { isTauri } from '@/tauri/utils';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { createModel } from '@rematch/core';
import { getVersion } from '@tauri-apps/api/app';
import { getDefaultStore } from 'jotai';
import { type RootModel } from '.';

const notifyInstallFailed = () => {
  toast({
    tone: 'destructive',
    title: formatMessageOutsideReact(updateFailureMessages.installFailedTitle),
    description: formatMessageOutsideReact(updateFailureMessages.installFailed),
  });
};

export type InitialState = {
  evaluationResult: PolicyEvaluationResult | null;
  currentPolicy: ForceUpdatePolicy | null;
  lastChecked: string | null;
  checkIntervalId: ReturnType<typeof setInterval> | null;
  isInstalling: boolean; // Manual loading state for update installation
};

const initialState: InitialState = {
  evaluationResult: null,
  currentPolicy: null,
  lastChecked: null,
  checkIntervalId: null,
  isInstalling: false,
};

export const forceUpdateModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setEvaluationResult(state, payload: PolicyEvaluationResult | null) {
      return { ...state, evaluationResult: payload };
    },
    setCurrentPolicy(state, payload: ForceUpdatePolicy | null) {
      return { ...state, currentPolicy: payload };
    },
    setLastChecked(state, payload: string | null) {
      return { ...state, lastChecked: payload };
    },
    setCheckIntervalId(state, payload: ReturnType<typeof setInterval> | null) {
      return { ...state, checkIntervalId: payload };
    },
    setIsInstalling(state, payload: boolean) {
      return { ...state, isInstalling: payload };
    },
    clear() {
      return initialState;
    },
  },
  effects: (dispatch) => ({
    async checkForceUpdatePolicy() {
      if (!isTauri()) {
        return;
      }

      // Fail-closed: the regional gate is ON but the flag provider or region
      // has not resolved yet — no enforcement until the feed resolves; the
      // periodic check re-evaluates once it does.
      if (resolveUpdaterFeed().kind === 'blocked') {
        dispatch.forceUpdateModel.setEvaluationResult(null);
        dispatch.forceUpdateModel.setCurrentPolicy(null);
        dispatch.forceUpdateModel.setLastChecked(new Date().toISOString());
        logger.info('Force update check skipped — updater feed unresolved');
        return;
      }

      try {
        const policy = await fetchPolicyFromRemoteConfig();
        if (!policy) {
          // No policy = no enforcement
          dispatch.forceUpdateModel.setEvaluationResult(POLICY_EVALUATION.OK);
          dispatch.forceUpdateModel.setCurrentPolicy(null);
          dispatch.forceUpdateModel.setLastChecked(new Date().toISOString());
          dispatch.forceUpdateModel.setIsInstalling(false);
          return;
        }

        // Get current version
        const currentVersion = await getVersion();

        // Get recording status from timer model + Chronicle session atom (covers paused Chronicle)
        const isRecording =
          store.getState().timerModel.isRunning ||
          getDefaultStore().get(chronicleSessionIdAtom) !== null;

        // Evaluate policy using the latest fetched policy
        const evaluation = evaluatePolicy({
          currentVersion,
          policy,
          isRecording,
        });

        // Update state
        dispatch.forceUpdateModel.setEvaluationResult(evaluation.result);
        dispatch.forceUpdateModel.setCurrentPolicy(policy);
        dispatch.forceUpdateModel.setLastChecked(new Date().toISOString());

        // Track analytics for modal view
        if (
          evaluation.result === POLICY_EVALUATION.FORCED ||
          evaluation.result === POLICY_EVALUATION.GRACE
        ) {
          trackForceUpdateViewed({
            update_type: evaluation.result,
            reason: evaluation.reason!,
            current_version: currentVersion,
            min_supported_version: policy.min_supported_version,
            deadline_at: evaluation.deadlineAt?.toISOString(),
          });
        }

        logger.info('Force update policy checked', {
          result: evaluation.result,
          currentVersion,
          minSupportedVersion: policy.min_supported_version,
        });
      } catch (error) {
        logger.error('Error checking force update policy', { error });
      }
    },

    /**
     * Start periodic policy checks based on policy_check_in_hours
     */
    async startPeriodicCheck() {
      if (!isTauri()) {
        return;
      }

      // Stop any existing interval first (defensive, should not happen)
      dispatch.forceUpdateModel.stopPeriodicCheck();

      // Initial check
      await dispatch.forceUpdateModel.checkForceUpdatePolicy();

      // Get check interval from policy (validated to be 1-24h by Zod schema)
      const policy = store.getState().forceUpdateModel.currentPolicy;
      const checkHours = policy?.policy_check_in_hours ?? 6;
      const intervalMs = checkHours * 60 * 60 * 1000;

      logger.info('Starting force update periodic check', {
        intervalHours: checkHours,
      });

      // Set up interval
      const intervalId = setInterval(() => {
        void dispatch.forceUpdateModel.checkForceUpdatePolicy();
      }, intervalMs);

      dispatch.forceUpdateModel.setCheckIntervalId(intervalId);
    },

    /**
     * Stop periodic policy checks
     */
    stopPeriodicCheck() {
      if (!isTauri()) {
        return;
      }

      const intervalId = store.getState().forceUpdateModel.checkIntervalId;
      if (intervalId) {
        clearInterval(intervalId);
        dispatch.forceUpdateModel.setCheckIntervalId(null);
        logger.info('Stopped force update periodic check');
      }
    },

    /**
     * Handle "Update Now" button click
     * Triggers update installation via tauriUpdateModel
     */
    async handleUpdateNow() {
      if (!isTauri()) {
        return;
      }

      try {
        // Set installing state - will remain true until app restarts
        dispatch.forceUpdateModel.setIsInstalling(true);

        // Get current state for analytics
        const currentVersion = await getVersion();
        const { currentPolicy, evaluationResult } = store.getState().forceUpdateModel;

        if (
          currentPolicy &&
          (evaluationResult === POLICY_EVALUATION.FORCED ||
            evaluationResult === POLICY_EVALUATION.GRACE)
        ) {
          // Track update initiated
          trackForceUpdateInitiated({
            update_type: evaluationResult,
            current_version: currentVersion,
            min_supported_version: currentPolicy.min_supported_version,
          });
        }

        logger.info('User initiated force update', {
          currentVersion,
          policy: currentPolicy,
        });

        // Install through the same fail-closed feed routing as the optional
        // updater, so a forced install can never exceed what the region's
        // channel has published.
        const feed = resolveUpdaterFeed();
        const update = await checkUpdateForFeed(feed);
        if (!update) {
          // No update on the resolved feed despite a forced policy. Don't
          // leave the button spinning forever: reset and tell the user so
          // they can retry — the modal stays up (policy still unsatisfied).
          logger.warn('Force update: no update available on the resolved feed');
          notifyInstallFailed();
          dispatch.forceUpdateModel.setIsInstalling(false);
          return;
        }
        const installed = await installUpdateForFeed(feed);
        if (!installed) {
          logger.warn('Force update: install found no update to apply');
          notifyInstallFailed();
          dispatch.forceUpdateModel.setIsInstalling(false);
          return;
        }
        trackTauriUpdate(update.version);
        // relaunch() ends the process. Keep `isInstalling` true through the
        // restart window so the button stays disabled — resetting it here
        // would re-enable a double-click before the process exits.
        await dispatch.tauriUpdateModel.handleRelaunchApp();
      } catch (error) {
        logger.error('Failed to install force update', { error });
        notifyInstallFailed();
        dispatch.forceUpdateModel.setIsInstalling(false);
      }
    },

    /**
     * Handle "Later" button click during grace period
     * Tracks deferred action and hides modal until next check
     */
    async deferUpdate() {
      if (!isTauri()) {
        return;
      }

      try {
        const currentVersion = await getVersion();
        const { currentPolicy } = store.getState().forceUpdateModel;

        if (currentPolicy) {
          const deadlineAt = new Date(currentPolicy.force_update_deadline);
          // Track deferred action
          trackForceUpdateDeferred({
            current_version: currentVersion,
            min_supported_version: currentPolicy.min_supported_version,
            deadline_at: deadlineAt.toISOString(),
          });
        }

        // Clear evaluation result - next periodic check will re-evaluate
        dispatch.forceUpdateModel.setEvaluationResult(null);

        logger.info('User deferred force update', {
          currentVersion,
        });
      } catch (error) {
        logger.error('Failed to defer force update', { error });
      }
    },
  }),
});
